/**
 * Export pagination — pure geometry, no DOM.
 *
 * computePageBreaks turns "slice every pageHeight px" into break offsets that
 * avoid cutting through text lines / atomic elements: each nominal boundary is
 * nudged UP into the nearest horizontal gap between rects, bounded by
 * minFillRatio so a rect-dense page can never shrink below that fraction of
 * the page height (a rect taller than the page is unsplittable — nominal cut).
 *
 * parsePageRange parses the dialog's "1-3,5" page-range input.
 */

/** A horizontal band occupied by content that should not be cut (CSS px). */
export interface BreakRect {
  top: number
  bottom: number
}

export interface PageBreakOptions {
  /** Never shrink a page below this fraction of pageHeight (default 0.6). */
  minFillRatio?: number
}

/**
 * Compute page-bottom offsets (ascending, last === contentHeight).
 * `rects` need not be sorted; zero/negative-height rects are ignored.
 */
export function computePageBreaks(
  rects: BreakRect[],
  contentHeight: number,
  pageHeight: number,
  opts?: PageBreakOptions,
): number[] {
  if (!(contentHeight > 0)) return [0]
  if (!(pageHeight > 0) || contentHeight <= pageHeight) return [contentHeight]

  const minFill = Math.max(0.1, Math.min(1, opts?.minFillRatio ?? 0.6))
  const sorted = rects
    .filter((r) => r.bottom > r.top)
    .sort((a, b) => a.top - b.top)

  const breaks: number[] = []
  let pageTop = 0
  // Guard: strictly monotonic progress, so the loop always terminates.
  while (contentHeight - pageTop > pageHeight) {
    const nominal = pageTop + pageHeight
    const minBreak = pageTop + pageHeight * minFill
    let cut = nominal
    // Find rects straddling the nominal cut and nudge above the highest one
    // that still respects minFill. Rects are few (lines+atoms per page), so a
    // linear scan per page is fine.
    for (let guard = 0; guard < 50; guard++) {
      const straddler = sorted.find((r) => r.top < cut && r.bottom > cut)
      if (!straddler) break
      if (straddler.top <= minBreak) { cut = nominal; break } // unsplittable region — accept the nominal cut
      cut = straddler.top
    }
    breaks.push(cut)
    pageTop = cut
  }
  breaks.push(contentHeight)
  return breaks
}

/**
 * Parse a "1-3,5,8-9" page-range string against a known page count.
 * Returns a sorted, deduped array of 1-based page numbers, or null when the
 * string is invalid / selects nothing. An empty/whitespace string means "all
 * pages" and returns null too — callers treat null as no-filter.
 */
export function parsePageRange(input: string, pageCount: number): number[] | null {
  const s = input.trim()
  if (!s) return null
  if (!/^[0-9,\s-]+$/.test(s)) return null
  const pages = new Set<number>()
  for (const part of s.split(',')) {
    const p = part.trim()
    if (!p) continue
    const m = p.match(/^(\d+)(?:\s*-\s*(\d+))?$/)
    if (!m) return null
    const from = parseInt(m[1], 10)
    const to = m[2] !== undefined ? parseInt(m[2], 10) : from
    if (from < 1 || to < from) return null
    for (let i = from; i <= Math.min(to, pageCount); i++) pages.add(i)
  }
  if (!pages.size) return null
  return [...pages].sort((a, b) => a - b)
}
